<script setup>
// RM-427: Admin → Applications. One place to see and change what each way of
// joining RM asks:
//   Paths      each acquisition path's default Show / Required per question
//   Questions  the shared question library (edit wording, add, retire)
//   RM via text  the switch for the RM via text choice on the application
// A group's own changes are made from that group (Group Accounts → the group →
// Application questions); groups inherit their path's defaults otherwise.
import { computed, reactive, ref } from 'vue'
import axios from '@axios'
import ApplicationPreviewDialog from '@core/components/application/ApplicationPreviewDialog.vue'
import { STEP_ORDER, STEP_TITLES } from '@/utils/applicationForm'

const TYPE_LABELS = {
  text: 'Short text',
  email: 'Email',
  phone: 'Mobile number',
  textarea: 'Long text',
  yesno: 'Yes / No',
  select: 'Choice',
  url: 'Link',
  restaurants: 'Restaurant search',
}

const tab = ref('paths')
const loading = ref(true)
const loadError = ref('')
const questions = ref([])
const paths = ref([])
const newQuestionTypes = ref([])
const smsSetting = ref(null)
const toast = reactive({ show: false, text: '', color: 'success' })

const notify = (text, color = 'success') => {
  toast.text = text
  toast.color = color
  toast.show = true
}

// ── Paths ─────────────────────────────────────────────────────────────────────
const selectedPath = ref('activate')
const draft = reactive({}) // key → { show, required } for the selected path
const savingPath = ref(false)

const pathInfo = computed(() => paths.value.find(p => p.path === selectedPath.value) || null)

const loadDraft = () => {
  Object.keys(draft).forEach(k => delete draft[k])
  const saved = new Map(((pathInfo.value && pathInfo.value.questions) || []).map(e => [e.key, e]))
  questions.value.forEach(q => {
    const e = saved.get(q.key) || { show: false, required: false }
    draft[q.key] = { show: q.locked ? true : !!e.show, required: q.locked ? true : !!e.required }
  })
}

const pathDirty = computed(() => {
  const saved = new Map(((pathInfo.value && pathInfo.value.questions) || []).map(e => [e.key, e]))

  return questions.value.some(q => {
    if (q.locked)
      return false
    const e = saved.get(q.key) || { show: false, required: false }
    const d = draft[q.key] || {}

    return !!e.show !== !!d.show || (!!d.show && !!e.required !== !!d.required)
  })
})

const activeQuestions = computed(() => questions.value.filter(q => !q.archived))

const questionsByStep = computed(() => STEP_ORDER.map(step => ({
  step,
  title: STEP_TITLES[step],
  questions: activeQuestions.value.filter(q => q.step === step),
})).filter(g => g.questions.length))

const followsHidden = q => q.showIf && q.showIf.key && draft[q.showIf.key] && !draft[q.showIf.key].show

const pathEntries = () => questions.value
  .filter(q => !q.locked)
  .map(q => ({ key: q.key, show: !!draft[q.key]?.show, required: !!draft[q.key]?.show && !!draft[q.key]?.required }))

const selectPath = path => {
  selectedPath.value = path
  loadDraft()
}

const savePath = async () => {
  savingPath.value = true
  try {
    await axios.put(`/application/admin/paths/${selectedPath.value}`, { questions: pathEntries() })
    await load({ keepPath: true })
    notify(`${pathInfo.value?.label || 'Path'} saved`)
  } catch (e) {
    notify(e?.response?.data?.error || 'Could not save', 'error')
  } finally {
    savingPath.value = false
  }
}

// ── Preview ───────────────────────────────────────────────────────────────────
const previewOpen = ref(false)
const previewRequest = ref(null)

const previewPath = () => {
  previewRequest.value = { path: selectedPath.value, pathQuestions: pathEntries() }
  previewOpen.value = true
}

// ── Questions ─────────────────────────────────────────────────────────────────
const editOpen = ref(false)
const editing = reactive({ key: '', label: '', hint: '', placeholder: '', options: '', order: 0, type: '', locked: false, archived: false, isNew: false, step: 'details', showIfKey: null, showIfEquals: '' })
const savingQuestion = ref(false)

const openEdit = q => {
  Object.assign(editing, {
    key: q.key,
    label: q.label,
    hint: q.hint || '',
    placeholder: q.placeholder || '',
    options: (q.options || []).join('\n'),
    order: q.order,
    type: q.type,
    locked: q.locked,
    archived: !!q.archived,
    isNew: false,
    step: q.step,
    showIfKey: q.showIf?.key || null,
    showIfEquals: q.showIf?.equals || '',
  })
  editOpen.value = true
}

const openAdd = () => {
  Object.assign(editing, { key: '', label: '', hint: '', placeholder: '', options: '', order: 900, type: 'text', locked: false, archived: false, isNew: true, step: 'details', showIfKey: null, showIfEquals: '' })
  editOpen.value = true
}

const parentChoices = computed(() => questions.value
  .filter(q => !q.archived && (q.type === 'yesno' || q.type === 'select') && q.key !== editing.key)
  .map(q => ({ title: q.label, value: q.key })))

const saveQuestion = async () => {
  savingQuestion.value = true
  const body = {
    label: editing.label,
    hint: editing.hint,
    placeholder: editing.placeholder,
    order: Number(editing.order),
    options: editing.options.split('\n').map(s => s.trim()).filter(Boolean),
  }
  try {
    if (editing.isNew) {
      await axios.post('/application/admin/questions', {
        ...body,
        key: editing.key,
        type: editing.type,
        step: editing.step,
        showIf: editing.showIfKey ? { key: editing.showIfKey, equals: editing.showIfEquals } : null,
      })
      notify('Question added. Show it on a path or group to ask it.')
    } else {
      await axios.put(`/application/admin/questions/${editing.key}`, { ...body, archived: editing.archived })
      notify('Question saved')
    }
    editOpen.value = false
    await load({ keepPath: true })
  } catch (e) {
    notify(e?.response?.data?.error || 'Could not save', 'error')
  } finally {
    savingQuestion.value = false
  }
}

const usedOn = q => paths.value
  .filter(p => (p.questions || []).some(e => e.key === q.key && e.show) || q.locked)
  .map(p => p.label)

// ── RM via text switch ────────────────────────────────────────────────────────
const savingSms = ref(false)

const setSms = async enabled => {
  savingSms.value = true
  try {
    const { data } = await axios.put('/application/admin/sms-opt-in', { enabled })
    smsSetting.value = { ...smsSetting.value, ...data }
    notify(enabled ? 'RM via text is now offered on applications' : 'RM via text is no longer offered on applications')
  } catch (e) {
    notify(e?.response?.data?.error || 'Could not change the switch', 'error')
  } finally {
    savingSms.value = false
  }
}

// ── load ──────────────────────────────────────────────────────────────────────
async function load({ keepPath = false } = {}) {
  loadError.value = ''
  try {
    const { data } = await axios.get('/application/admin')
    questions.value = data.questions || []
    paths.value = data.paths || []
    newQuestionTypes.value = data.newQuestionTypes || []
    smsSetting.value = data.smsOptIn || null
    if (!keepPath && paths.value.length)
      selectedPath.value = paths.value[0].path
    loadDraft()
  } catch (e) {
    loadError.value = e?.response?.data?.error || 'Could not load the application settings'
  } finally {
    loading.value = false
  }
}

load()
</script>

<template>
  <div class="content-wrapper apps-admin">
    <div class="apps-admin__header">
      <h1 class="apps-admin__title">
        Applications
      </h1>
      <p class="apps-admin__lede">
        What each way of joining RM asks. Groups inherit their path's settings and can change them from their own page.
      </p>
    </div>

    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
    />
    <VAlert
      v-else-if="loadError"
      type="error"
      variant="tonal"
    >
      {{ loadError }}
    </VAlert>

    <template v-else>
      <VTabs
        v-model="tab"
        class="mb-4"
      >
        <VTab value="paths">
          Paths
        </VTab>
        <VTab value="questions">
          Questions
        </VTab>
        <VTab value="text">
          RM via text
        </VTab>
      </VTabs>

      <!-- ── Paths ───────────────────────────────────────────────────────── -->
      <div v-if="tab === 'paths'">
        <div class="apps-admin__paths">
          <VChip
            v-for="p in paths"
            :key="p.path"
            :color="p.path === selectedPath ? 'primary' : undefined"
            :variant="p.path === selectedPath ? 'flat' : 'outlined'"
            class="mr-2 mb-2"
            @click="selectPath(p.path)"
          >
            {{ p.label }}
          </VChip>
        </div>

        <div class="apps-admin__actions">
          <span class="apps-admin__meta">
            <template v-if="pathInfo && pathInfo.updatedBy && pathInfo.updatedAt">Last changed {{ new Date(pathInfo.updatedAt).toLocaleDateString() }} by {{ pathInfo.updatedBy }}</template>
            <template v-else>As the form was before Applications</template>
          </span>
          <VSpacer />
          <VBtn
            variant="text"
            @click="previewPath"
          >
            Preview
          </VBtn>
          <VBtn
            color="primary"
            :disabled="!pathDirty"
            :loading="savingPath"
            @click="savePath"
          >
            Save
          </VBtn>
        </div>

        <section
          v-for="group in questionsByStep"
          :key="group.step"
          class="apps-admin__screen"
        >
          <h2 class="apps-admin__screen-title">
            {{ group.title }}
          </h2>
          <div
            v-for="q in group.questions"
            :key="q.key"
            class="apps-admin__row"
          >
            <div class="apps-admin__q">
              <div class="apps-admin__q-label">
                {{ q.label }}
              </div>
              <div class="apps-admin__q-meta">
                {{ TYPE_LABELS[q.type] || q.type }}<template v-if="q.showIf && q.showIf.key">
                  · asked when "{{ (questions.find(x => x.key === q.showIf.key) || {}).label }}" is {{ q.showIf.equals }}
                </template>
                <template v-if="followsHidden(q) && draft[q.key]?.show">
                  · hidden: the question it follows is hidden
                </template>
              </div>
            </div>
            <template v-if="q.locked">
              <span class="apps-admin__always">Always asked, required</span>
            </template>
            <template v-else>
              <VSwitch
                v-model="draft[q.key].show"
                label="Show"
                color="primary"
                density="compact"
                hide-details
                class="apps-admin__switch"
              />
              <VSwitch
                v-model="draft[q.key].required"
                label="Required"
                color="primary"
                density="compact"
                hide-details
                :disabled="!draft[q.key].show"
                class="apps-admin__switch"
              />
            </template>
          </div>
        </section>
      </div>

      <!-- ── Questions ───────────────────────────────────────────────────── -->
      <div v-if="tab === 'questions'">
        <div class="apps-admin__actions">
          <span class="apps-admin__meta">
            Changing a question's wording changes it everywhere it's asked. Answers keep the wording they were given with.
          </span>
          <VSpacer />
          <VBtn
            color="primary"
            @click="openAdd"
          >
            Add question
          </VBtn>
        </div>
        <section
          v-for="step in STEP_ORDER"
          :key="step"
          class="apps-admin__screen"
        >
          <h2 class="apps-admin__screen-title">
            {{ STEP_TITLES[step] }}
          </h2>
          <div
            v-for="q in questions.filter(x => x.step === step)"
            :key="q.key"
            class="apps-admin__row"
            :class="{ 'apps-admin__row--retired': q.archived }"
          >
            <div class="apps-admin__q">
              <div class="apps-admin__q-label">
                {{ q.label }}
              </div>
              <div class="apps-admin__q-meta">
                {{ TYPE_LABELS[q.type] || q.type }} · {{ q.key }} · v{{ q.version }}
                <template v-if="q.archived">
                  · retired
                </template>
                <template v-else-if="usedOn(q).length">
                  · asked on {{ usedOn(q).length }} {{ usedOn(q).length === 1 ? 'path' : 'paths' }}
                </template>
                <template v-else>
                  · not asked on any path
                </template>
              </div>
            </div>
            <VBtn
              variant="text"
              size="small"
              @click="openEdit(q)"
            >
              Edit
            </VBtn>
          </div>
        </section>
      </div>

      <!-- ── RM via text ─────────────────────────────────────────────────── -->
      <div
        v-if="tab === 'text' && smsSetting"
        class="apps-admin__text"
      >
        <VSwitch
          :model-value="smsSetting.enabled"
          color="primary"
          :loading="savingSms"
          hide-details
          :label="smsSetting.enabled ? 'On: applications offer RM via text' : 'Off: applications don\'t offer RM via text'"
          @update:model-value="setSms"
        />
        <p class="apps-admin__body">
          When it's on, the application shows an optional, unticked RM via text choice under the mobile number. Ticking it enrolls that number; texting turns on, with the welcome text and the "RM via text is on" email, when the member activates their account. Nothing is texted before then.
        </p>
        <p class="apps-admin__body">
          <template v-if="!smsSetting.saved">
            Not set here yet, so it is {{ smsSetting.default ? 'on (outside production)' : 'off (production)' }} by default.
          </template>
          Keep it off in production until Twilio approves the campaign amendment that describes this opt-in.
        </p>
        <div
          v-if="smsSetting.copy"
          class="apps-admin__copy"
        >
          <div class="apps-admin__q-label">
            {{ smsSetting.copy.label }}
          </div>
          <p class="apps-admin__q-meta">
            {{ smsSetting.copy.disclosure }} Terms · Privacy Policy
          </p>
        </div>
        <p class="apps-admin__meta">
          Wording: CMS keys loginJoin.smsOptIn.label and loginJoin.smsOptIn.disclosure. The exact text shown is saved with each choice.
        </p>
      </div>
    </template>

    <!-- Edit / add question -->
    <VDialog
      v-model="editOpen"
      max-width="560"
    >
      <VCard>
        <VCardTitle class="pt-4">
          {{ editing.isNew ? 'Add a question' : 'Edit question' }}
        </VCardTitle>
        <VCardText>
          <template v-if="editing.isNew">
            <VTextField
              v-model="editing.key"
              label="Key (never changes; letters, numbers, _)"
              class="mb-3"
            />
            <VSelect
              v-model="editing.type"
              :items="newQuestionTypes.map(t => ({ title: TYPE_LABELS[t] || t, value: t }))"
              label="Answer type"
              class="mb-3"
            />
            <VSelect
              v-model="editing.step"
              :items="STEP_ORDER.map(s => ({ title: STEP_TITLES[s], value: s }))"
              label="Screen"
              class="mb-3"
            />
          </template>
          <VTextField
            v-model="editing.label"
            label="Question"
            class="mb-3"
          />
          <VTextField
            v-model="editing.hint"
            label="Hint (optional)"
            class="mb-3"
          />
          <VTextarea
            v-if="editing.type === 'select'"
            v-model="editing.options"
            label="Choices, one per line"
            rows="4"
            class="mb-3"
          />
          <VTextField
            v-model="editing.order"
            label="Position on its screen (lower = earlier)"
            type="number"
            class="mb-3"
          />
          <template v-if="editing.isNew">
            <VSelect
              v-model="editing.showIfKey"
              :items="parentChoices"
              label="Only ask when this question… (optional)"
              clearable
              class="mb-3"
            />
            <VTextField
              v-if="editing.showIfKey"
              v-model="editing.showIfEquals"
              label="…is answered (e.g. Yes)"
              class="mb-3"
            />
          </template>
          <VSwitch
            v-if="!editing.isNew && !editing.locked"
            v-model="editing.archived"
            color="primary"
            label="Retired (removed from every application; past answers are kept)"
            hide-details
          />
          <p
            v-if="editing.locked"
            class="apps-admin__meta mt-2"
          >
            Name, email and mobile number are asked on every application.
          </p>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="editOpen = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            :loading="savingQuestion"
            @click="saveQuestion"
          >
            Save
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <ApplicationPreviewDialog
      v-model="previewOpen"
      :request="previewRequest"
      :title="`Preview: ${pathInfo ? pathInfo.label : ''}`"
    />

    <VSnackbar
      v-model="toast.show"
      :color="toast.color"
      timeout="3000"
    >
      {{ toast.text }}
    </VSnackbar>
  </div>
</template>

<style lang="scss" scoped>
.apps-admin {
  max-width: 880px;
  padding-top: 24px;
  padding-bottom: 48px;
}

.apps-admin__header {
  margin-bottom: 16px;
}

.apps-admin__title {
  font-size: 24px;
  font-weight: 600;
  color: var(--ds-text-strong);
}

.apps-admin__lede,
.apps-admin__body {
  margin-top: 4px;
  font-size: 16px;
  line-height: 1.5;
  color: var(--ds-text-muted);
}

.apps-admin__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 16px;
}

.apps-admin__meta {
  font-size: 14px;
  color: var(--ds-text-muted);
}

.apps-admin__screen {
  margin-bottom: 24px;
}

.apps-admin__screen-title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--ds-text-muted);
}

.apps-admin__row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: thin solid var(--ds-border-light);
}

.apps-admin__row--retired {
  opacity: 0.55;
}

.apps-admin__q {
  flex: 1;
  min-width: 0;
}

.apps-admin__q-label {
  font-size: 16px;
  color: var(--ds-text-strong);
}

.apps-admin__q-meta {
  margin-top: 2px;
  font-size: 14px;
  color: var(--ds-text-muted);
}

.apps-admin__always {
  font-size: 14px;
  color: var(--ds-text-muted);
}

.apps-admin__switch {
  flex: 0 0 auto;
}

.apps-admin__copy {
  margin: 16px 0;
  padding: 16px;
  border-radius: 12px;
  background: var(--ds-surface-subtle, rgba(0, 0, 0, 0.03));
}

@media (max-width: 600px) {
  .apps-admin__row {
    flex-wrap: wrap;
  }

  .apps-admin__q {
    flex-basis: 100%;
  }
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
  # Platform-admin-only: every /application/admin endpoint enforces requireAdmin().
  requiresAdmin: true
</route>
